<template>
  <table-column-picker
    v-slot="{ headers, activatorProps }"
    :headers="taskHeaders"
    storage-key="task-list-table"
    title="Налаштування відображення переліку завдань">
    <b-server-table
      :key="tableKey"
      :filter-props="{
        scope: 'task',
        hiddenActiveFilterKeys: ['statusId', 'isOverdue'],
        filterMap: [
          { key: 'search' },
          {
            key: 'statusId',
            directory: 'taskStatuses',
            component: 'select',
          },
          {
            key: 'typeId',
            directory: 'taskTypes',
            component: 'select',
          },
          {
            key: 'executors[]',
            directory: 'userList',
            component: 'autocomplete-multiple',
          },
          {
            key: 'completedDate',
            dateRangeKeys: ['startDate', 'endDate'],
            component: 'input-date-range',
          },
        ],
      }"
      :headers="headers"
      :request-func="getTaskList"
      @click:row="openRow">
      <template #action-button>
        <div class="d-flex justify-end mb-3 gap-3">
          <printed-form></printed-form>
          <table-column-picker-button v-bind="activatorProps" />
          <v-btn
            v-if="!isAbsence && can('tasks.create')"
            rounded="lg"
            @click.stop="createTask">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Створити завдання
          </v-btn>
        </div>
      </template>
      <template #item.status="{ item }">
        <v-chip
          v-if="item.status"
          :color="getStatusColor(item.status.id)"
          size="small">
          <span class="font-weight-bold">{{ item.status?.name }}</span>
        </v-chip>
      </template>
      <template #item.deadline="{ item }">
        <v-chip v-if="item.isOverdue" color="red" size="small"
          >{{
            new CustomDate(item.deadline).toString({
              time: true,
              format: 'locale-hybrid',
            })
          }}
        </v-chip>
        <span v-else>{{
          new CustomDate(item.deadline).toString({
            time: true,
            format: 'locale-hybrid',
          })
        }}</span>
      </template>
      <template #item.executor="{ item }">
        <span>{{
          getPersonName(item.executor, { initials: true }) || '---'
        }}</span>
      </template>
      <template #item.topic="{ item }">
        <div class="d-flex align-center ga-1">
          <v-icon v-if="item.isHighImportance" color="error" size="small">
            mdi-star
          </v-icon>
          <span
            v-tooltip:bottom="item.topic"
            style="
              display: block;
              width: 200px;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            "
            >{{ item?.topic || '---' }}
          </span>
        </div>
      </template>
      <template #item.author="{ item }">
        <span>{{
          getPersonName(item.author, { initials: true }) || '---'
        }}</span>
      </template>
      <template #item.completedDate="{ item }">
        <div class="d-flex align-center gap-3">
          <span>{{
            new CustomDate(item.completedDate).toString({ time: true }) || '---'
          }}</span>
          <v-menu v-if="item.completedDate" open-on-hover>
            <template #activator="{ props }">
              <v-btn
                density="compact"
                icon="mdi-information-variant"
                size="small"
                v-bind="props"
                variant="plain"></v-btn>
            </template>

            <v-card>
              <v-card-text>
                {{ item.completionReport || item.rejectComment }}
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </template>
      <template #item.connect="{ item }">
        <span
          v-if="item.entityTypeId && item.entityCustomId"
          class="link"
          @click.stop="
            $router.push({
              name:
                item.entityTypeId === 3
                  ? 'single-order'
                  : item.entityTypeId === 9
                  ? 'audit-single'
                  : 'correspondence-single',
              params: { id: item.entityCustomId },
            })
          ">
          <span v-if="item.entityTypeId === 3">Наказ</span>
          <span v-else-if="item.entityTypeId === 9">Аудит</span>
          <span v-else>Кореспонденція</span>
        </span>
        <v-icon v-else>{{ 'mdi-minus' }}</v-icon>
      </template>
      <template #[`item.contractor`]="{ item }">
        <v-tooltip v-if="item.contractor" :text="item.contractor?.name" bottom>
          <template #activator="{ props }">
            <span
              class="link"
              style="
                display: block;
                width: 230px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              "
              v-bind="props"
              @click.stop="
                $router.push({
                  name: 'single-contractor',
                  params: { id: item?.contractor?.id },
                })
              "
              >{{ item.contractor?.name }}</span
            >
          </template>
        </v-tooltip>
        <v-icon v-else>{{ 'mdi-minus' }}</v-icon>
      </template>
      <template #item.actions="{ item }">
        <b-action-menu
          :actions="[
            {
              title: 'Взяти в роботу',
              action: () => {
                actions.taskTakeToWork(item, item.id).then(setTask)
              },
              icon: 'mdi-play',
              disabled: actions.disabled(item).takeToWork.value,
              iconProps: {
                color: 'blue-darken-3',
              },
            },
            {
              title: 'Завершити завдання',
              action: () => actions.taskComplete(item, item.id, setTask),
              icon: 'mdi-bell-check-outline',
              disabled: actions.disabled(item).complete.value,
              iconProps: {
                color: 'success',
              },
            },
            {
              title: 'Копіювати завдання',
              action: () => actions.copyTask(item),
              icon: 'mdi-content-copy',
              disabled: actions.disabled(item).copy.value,
              iconProps: {
                color: 'warning',
              },
            },
            {
              title: 'Відхилити завдання',
              action: () => actions.taskReject(item, item.id, setTask),
              icon: 'mdi-clock-out',
              disabled: actions.disabled(item).reject.value,
              iconProps: {
                color: 'error',
              },
            },
            {
              title: 'Видалити завдання',
              action: () => actions.taskDelete(item, item.id, taskDelete),
              icon: 'mdi-delete-forever',
              disabled: actions.disabled(item).del.value,
              iconProps: {
                color: 'error',
              },
            },
          ]">
        </b-action-menu>
      </template>
      <template #bottom></template>
    </b-server-table>
  </table-column-picker>
</template>

<script>
import { useBreadcrumb, useRoute } from '@/plugins'
import { computed, ref } from 'vue'
import {
  taskAbsenceIncomingList,
  taskAbsenceOutgoingList,
  taskIncomingList,
  taskOutgoingList,
} from '@/requests/task'
import { navigateToItem } from '@/utils/helpers.js'
import { useTaskActions } from '@/pages/task/helpers/taskActions.js'
import { taskHeaders } from '@/utils/tableHeaders'
import PrintedForm from '@/pages/task/single/components/PrintedForm.vue'
import { BActionMenu, BServerTable } from 'best-modules/components/index'
import { openDialog } from '@/dialog'
import { useRouter } from 'vue-router'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { useCan } from '@/composables/useCan'
import TableColumnPicker from '@/components/TableColumnPicker.vue'
import TableColumnPickerButton from '@/components/buttons/TableColumnPickerButton.vue'

export default {
  components: {
    TableColumnPickerButton,
    TableColumnPicker,
    BActionMenu,
    PrintedForm,
    BServerTable,
  },
  data: () => {
    return {
      taskHeaders,
    }
  },
  methods: { getPersonName, CustomDate },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { can } = useCan()
    const renderKey = ref(1)
    const tableKey = computed(() => {
      const query = Object.fromEntries(
        Object.entries(route.query).filter(([key]) => key !== 'page')
      )

      return `${JSON.stringify(query)}-${renderKey.value}`
    })
    const taskList = ref([])
    const isAbsence = [
      'absence-single-task',
      'absence-outgoing-tasks',
      'absence-incoming-tasks',
    ].includes(route.name)
    const breadcrumb = useBreadcrumb()
    breadcrumb.set([], { type: 'replace' })

    const url = computed(() => {
      switch (route.name) {
        case 'incoming-tasks':
          return taskIncomingList
        case 'outgoing-tasks':
          return taskOutgoingList
        case 'absence-incoming-tasks':
          return taskAbsenceIncomingList
        case 'absence-outgoing-tasks':
          return taskAbsenceOutgoingList
        default:
          return taskIncomingList
      }
    })

    const openRow = event => {
      if (isAbsence) {
        router.push({
          name: 'absence-single-task',
          params: {
            id: event.id,
            absenceId: route?.params?.absenceId,
          },
        })
      } else {
        navigateToItem('single-task', event.id)
      }
    }

    const getStatusColor = statusId => {
      switch (statusId) {
        case 1:
          return 'warning'
        case 2:
          return 'blue-darken-3'
        case 3:
          return 'success'
        case 4:
          return 'error'
        case 5:
          return 'warning'
      }
    }

    const getTaskList = query => {
      if (isAbsence) {
        return url.value(+route?.params?.absenceId, query)
      }

      return url.value(query)
    }

    const actions = useTaskActions()
    const setTask = () => {
      return (renderKey.value += 1)
    }
    const pushTask = task => taskList.value.push(task)
    const taskDelete = () => {
      return (renderKey.value += 1)
    }

    const createTask = () => {
      openDialog({
        name: 'CreateTask',
        dialogData: {},
        params: {},
      })
    }

    return {
      navigateToItem,
      getTaskList,
      renderKey,
      tableKey,
      openRow,
      actions,
      setTask,
      pushTask,
      createTask,
      isAbsence,
      taskDelete,
      getStatusColor,
      can,
    }
  },
}
</script>

<style lang="scss"></style>
